import React, { useState } from 'react';
import { Check, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import ContactModal from './ContactModal';

const AboutUs = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const stats = [
    { number: '300+', label: 'Successful Projects Delivered' },
    { number: '95%', label: 'Client Satisfaction Rate' },
    { number: '120+', label: 'Trusted Business Partners' },
    { number: '24/7', label: 'Dedicated Support' }
  ];

  const visionPoints = [
    'Innovative Digital Solutions',
    'Sustainable Business Growth',
    'Customer-Centric Approach',
    'Global Competitive Edge'
  ];

  const testimonials = [
    {
      id: 1,
      name: 'Terry M. Kane',
      text: 'Invotage built a custom website for my startup, and the results exceeded expectations. Clean design, fast performance, and an immediate boost in credibility.',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d'
    },
    {
      id: 2,
      name: 'Jayson L. Hurd',
      text: 'The mobile app Invotage developed for us was seamless, user-friendly, and reliable. It helped us engage customers more effectively and scale our digital presence.',
      image: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e'
    },
    {
      id: 3,
      name: 'Eric I. Atwell',
      text: 'I needed specialized software to streamline my operations. Invotage designed a tailored solution that was secure, scalable, and incredibly easy to use.',
      image: 'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-12">
            <div className="inline-block px-4 py-1 bg-purple-600/20 backdrop-blur-sm border border-purple-500/30 rounded-full text-sm text-purple-300 mb-4">
              ABOUT US
            </div>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6">
              About Us
            </h1>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Image - Shows First on Mobile */}
            <div className="relative order-1 lg:order-1">
              <div className="absolute inset-0 bg-gradient-to-tr from-purple-600/20 to-blue-600/20 rounded-3xl"></div>
              <img
                src="https://images.unsplash.com/photo-1522071820081-009f0129c71c"
                alt="Team collaboration"
                className="rounded-2xl lg:rounded-3xl shadow-2xl shadow-purple-900/50 w-full h-auto object-cover ring-1 ring-purple-500/20"
              />
            </div>

            {/* Content - Shows Below Image on Mobile */}
            <div className="space-y-4 lg:space-y-6 order-2 lg:order-2">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white">
                Building Digital Experiences That Drive Growth
              </h2>
              <p className="text-base lg:text-lg text-gray-300 leading-relaxed">
                We craft innovative digital solutions blending creativity, technology, and strategy to help businesses scale, thrive, and stay competitive in today's fast-changing world.
              </p>
              <p className="text-base lg:text-lg text-gray-300 leading-relaxed">
                From startups to Fortune 500 companies, we've helped over 300 businesses accelerate their digital transformation. Our commitment to excellence, innovation, and client success has earned us a 95% client satisfaction rate.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Vision Section */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-purple-900/10 to-transparent"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Image - Shows First on Mobile */}
            <div className="relative order-1 lg:order-2">
              <div className="absolute inset-0 bg-gradient-to-tr from-purple-600/20 to-blue-600/20 rounded-3xl"></div>
              <img
                src="https://images.unsplash.com/photo-1553877522-43269d4ea984"
                alt="Innovation and technology"
                className="rounded-2xl lg:rounded-3xl shadow-2xl shadow-purple-900/50 w-full h-auto object-cover ring-1 ring-purple-500/20"
              />
            </div>

            {/* Content - Shows Below Image on Mobile */}
            <div className="space-y-4 lg:space-y-6 order-2 lg:order-1">
              <div className="inline-block px-4 py-1 bg-purple-600/20 backdrop-blur-sm border border-purple-500/30 rounded-full text-sm text-purple-300 mb-4">
                OUR VISION
              </div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white">
                Shaping Tomorrow With Innovative Technology
              </h2>
              <p className="text-base lg:text-lg text-gray-300 leading-relaxed">
                Our vision is to empower businesses worldwide with future-ready digital solutions that drive innovation, enhance efficiency, and foster meaningful connections in an evolving digital landscape.
              </p>

              {/* Vision Points */}
              <div className="space-y-4 pt-4">
                {visionPoints.map((point, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-gray-300">{point}</span>
                  </div>
                ))}
              </div>

              <Link to="/services">
                <Button className="mt-6 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white px-8 py-6 rounded-full shadow-lg shadow-purple-500/50">
                  Explore Our Vision
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div
                key={index}
                className="text-center p-8 bg-gradient-to-br from-gray-800 to-gray-900 rounded-3xl border border-purple-500/20 shadow-lg shadow-purple-900/30 hover:shadow-purple-900/50 transition-all duration-300"
              >
                <div className="text-4xl lg:text-5xl font-bold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent mb-2">
                  {stat.number}
                </div>
                <div className="text-gray-400 text-sm lg:text-base">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-purple-900/10"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-1 bg-purple-600/20 backdrop-blur-sm border border-purple-500/30 rounded-full text-sm text-purple-300 mb-4">
              CLIENT STORIES
            </div>
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">
              What Our Clients Say
            </h2>
            <div className="h-1 w-32 bg-gradient-to-r from-purple-500 to-blue-500 mx-auto rounded-full"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div
                key={testimonial.id}
                className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-3xl p-8 border border-purple-500/20 shadow-lg shadow-purple-900/30 hover:shadow-purple-900/50 transition-all duration-300"
                style={{
                  animation: `fadeInUp 0.6s ease-out ${index * 0.2}s both`
                }}
              >
                <div className="flex items-center gap-4 mb-6">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-16 h-16 rounded-full object-cover ring-2 ring-purple-500/50"
                  />
                  <div>
                    <h4 className="text-white font-semibold">{testimonial.name}</h4>
                    <div className="flex gap-1 mt-1">
                      {[...Array(5)].map((_, i) => (
                        <svg key={i} className="w-4 h-4 fill-yellow-400" viewBox="0 0 20 20">
                          <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                        </svg>
                      ))}
                    </div>
                  </div>
                </div>
                <p className="text-gray-400 leading-relaxed">
                  "{testimonial.text}"
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-gradient-to-br from-purple-900/30 to-blue-900/30 rounded-3xl p-12 border border-purple-500/20 shadow-2xl shadow-purple-900/50">
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
              Ready to Transform Your Business?
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Let's discuss how Invotage can help you achieve your digital goals and drive meaningful growth.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => setIsModalOpen(true)}
                className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white px-8 py-6 text-lg rounded-full shadow-lg shadow-purple-500/50"
              >
                Get Started Today
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button 
                onClick={() => setIsModalOpen(true)}
                variant="outline" 
                className="border-2 border-purple-500 text-white hover:bg-purple-500/10 px-8 py-6 text-lg rounded-full"
              >
                View Our Services
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Modal */}
      <ContactModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)}
        title="Let's Work Together"
      />
    </div>
  );
};

export default AboutUs;
